
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Crown, AlertTriangle, TrendingUp } from "lucide-react";
import type { FBPost, IGPost } from "@/lib/types";
import { ImagePreview } from "@/components/cards/ImagePreview";
import { getPostTypeLabel } from "@/lib/post-type-labels";
import { ChartTooltip } from "./ChartTooltip";

function fmt(n: number) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString("cs-CZ");
}

function fmtPct(n: number) {
  return `${(n * 100).toFixed(1)}%`;
}

// ====== Engagement distribution histogram ======

const BUCKETS = [
  { label: "0-10", min: 0, max: 10 },
  { label: "10-50", min: 10, max: 50 },
  { label: "50-100", min: 50, max: 100 },
  { label: "100-500", min: 100, max: 500 },
  { label: "500-1k", min: 500, max: 1000 },
  { label: "1k-5k", min: 1000, max: 5000 },
  { label: "5k+", min: 5000, max: Infinity },
];

function makeHistogram(values: number[]): { name: string; value: number }[] {
  return BUCKETS.map((b) => ({
    name: b.label,
    value: values.filter((v) => v >= b.min && v < b.max).length,
  }));
}

interface DistributionProps {
  fbPosts: FBPost[];
  igPosts: IGPost[];
}

export function EngagementDistribution({ fbPosts, igPosts }: DistributionProps) {
  const data = useMemo(() => {
    const values: number[] = [];
    fbPosts.filter((p) => p.post_id).forEach((p) => values.push(p.page_post_engagements));
    igPosts.filter((p) => p.media_id).forEach((p) => values.push(p.media_engagement));
    return makeHistogram(values);
  }, [fbPosts, igPosts]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Distribuce engagementu</CardTitle>
        <p className="text-xs text-muted-foreground">Kolik příspěvků má engagement v dané úrovni</p>
      </CardHeader>
      <CardContent>
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#a89f93" }} stroke="#2e2e2e" />
              <YAxis tick={{ fontSize: 11, fill: "#a89f93" }} stroke="#2e2e2e" />
              <Tooltip content={<ChartTooltip isDate={false} />} />
              <Bar dataKey="value" name="Počet příspěvků" fill="#c9a84c" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// ====== Worst performing posts ======

interface WorstPostsProps {
  fbPosts?: FBPost[];
  igPosts?: IGPost[];
  limit?: number;
}

export function WorstPosts({ fbPosts = [], igPosts = [], limit = 5 }: WorstPostsProps) {
  const worstFb = useMemo(() => {
    return fbPosts
      .filter((p) => p.post_id && p.post_impressions_unique > 50) // ignore posts with no reach
      .sort((a, b) => a.page_post_engagements - b.page_post_engagements)
      .slice(0, limit);
  }, [fbPosts, limit]);

  const worstIg = useMemo(() => {
    return igPosts
      .filter((p) => p.media_id && p.media_reach > 50)
      .sort((a, b) => a.media_engagement - b.media_engagement)
      .slice(0, limit);
  }, [igPosts, limit]);

  const items: Array<{
    key: string;
    platform: "FB" | "IG";
    image: string | null;
    type: string | null;
    account: string;
    text: string;
    reach: number;
    engagement: number;
    rate: number;
    link: string | null;
  }> = [
    ...worstFb.map((p) => ({
      key: p.post_id || "",
      platform: "FB" as const,
      image: p.post_picture,
      type: p.type,
      account: p.account_name,
      text: p.post_message || "—",
      reach: p.post_impressions_unique,
      engagement: p.page_post_engagements,
      rate: p.post_impressions_unique > 0 ? p.page_post_engagements / p.post_impressions_unique : 0,
      link: p.permalink_url || (p.post_id ? `https://facebook.com/${p.post_id}` : null),
    })),
    ...worstIg.map((p) => ({
      key: p.media_id || "",
      platform: "IG" as const,
      image: p.media_thumbnail_url || p.media_url,
      type: p.media_product_type || p.media_type,
      account: p.account_name,
      text: p.media_caption || "—",
      reach: p.media_reach,
      engagement: p.media_engagement,
      rate: p.media_reach > 0 ? p.media_engagement / p.media_reach : 0,
      link: p.media_permalink,
    })),
  ].sort((a, b) => a.rate - b.rate).slice(0, limit);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          Nejhůř performující
        </CardTitle>
        <p className="text-xs text-muted-foreground">Posty s nejnižším engagement rate</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Žádná data</p>
        ) : (
          items.map((p, i) => (
            <div key={`${p.key}-${i}`} className="flex gap-3 p-2 rounded-lg bg-secondary/30">
              <ImagePreview src={p.image} fallbackType={p.type} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className={`text-[10px] ${p.platform === "FB" ? "border-[#c9a84c]" : "border-[#d4816b]"}`}>
                    {p.platform}
                  </Badge>
                  <span className="text-xs text-muted-foreground truncate">{p.account}</span>
                  <span className="text-[10px] text-muted-foreground">{getPostTypeLabel(p.type)}</span>
                </div>
                <p className="text-xs truncate mb-1">{p.text}</p>
                <div className="flex gap-3 text-[11px]">
                  <span className="text-muted-foreground">Dosah: <b className="text-foreground">{fmt(p.reach)}</b></span>
                  <span className="text-muted-foreground">Eng: <b className="text-foreground">{fmt(p.engagement)}</b></span>
                  <span className="text-red-400 font-semibold">{fmtPct(p.rate)}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

// ====== Restaurant leaderboard ======

interface LeaderboardProps {
  fbPosts?: FBPost[];
  igPosts?: IGPost[];
}

interface LeaderRow {
  account: string;
  platform: "FB" | "IG" | "both";
  posts: number;
  reach: number;
  engagement: number;
  avgEng: number;
  engRate: number;
}

export function RestaurantLeaderboard({ fbPosts = [], igPosts = [] }: LeaderboardProps) {
  const rows = useMemo(() => {
    const map = new Map<string, LeaderRow>();

    fbPosts.filter((p) => p.post_id).forEach((p) => {
      const key = `FB: ${p.account_name}`;
      const existing = map.get(key) || {
        account: p.account_name, platform: "FB" as const,
        posts: 0, reach: 0, engagement: 0, avgEng: 0, engRate: 0,
      };
      existing.posts++;
      existing.reach += p.post_impressions_unique;
      existing.engagement += p.page_post_engagements;
      map.set(key, existing);
    });

    igPosts.filter((p) => p.media_id).forEach((p) => {
      const key = `IG: ${p.account_name}`;
      const existing = map.get(key) || {
        account: p.account_name, platform: "IG" as const,
        posts: 0, reach: 0, engagement: 0, avgEng: 0, engRate: 0,
      };
      existing.posts++;
      existing.reach += p.media_reach;
      existing.engagement += p.media_engagement;
      map.set(key, existing);
    });

    return Array.from(map.values())
      .map((r) => ({
        ...r,
        avgEng: r.posts > 0 ? Math.round(r.engagement / r.posts) : 0,
        engRate: r.reach > 0 ? r.engagement / r.reach : 0,
      }))
      .sort((a, b) => b.avgEng - a.avgEng);
  }, [fbPosts, igPosts]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Crown className="h-4 w-4 text-[var(--primary)]" />
          Leaderboard restaurací
        </CardTitle>
        <p className="text-xs text-muted-foreground">Seřazeno dle průměrného engagementu na příspěvek</p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">#</TableHead>
              <TableHead>Účet</TableHead>
              <TableHead className="text-right">Posty</TableHead>
              <TableHead className="text-right">Dosah</TableHead>
              <TableHead className="text-right">Engagement</TableHead>
              <TableHead className="text-right">Ø eng/post</TableHead>
              <TableHead className="text-right">Eng. rate</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={`${row.platform}-${row.account}`}>
                <TableCell className="font-semibold">
                  {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-[10px] ${row.platform === "FB" ? "border-[#c9a84c]" : "border-[#d4816b]"}`}>
                      {row.platform}
                    </Badge>
                    <span className="text-sm">{row.account}</span>
                  </div>
                </TableCell>
                <TableCell className="text-right">{row.posts}</TableCell>
                <TableCell className="text-right">{fmt(row.reach)}</TableCell>
                <TableCell className="text-right">{fmt(row.engagement)}</TableCell>
                <TableCell className="text-right font-semibold text-[var(--primary)]">{fmt(row.avgEng)}</TableCell>
                <TableCell className="text-right">{fmtPct(row.engRate)}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-4">Žádná data</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ====== Viral ratio scatter-like visual ======

interface ViralProps {
  fbPosts?: FBPost[];
  igPosts?: IGPost[];
  limit?: number;
}

export function ViralPosts({ fbPosts = [], igPosts = [], limit = 5 }: ViralProps) {
  const items = useMemo(() => {
    const all: Array<{
      key: string; platform: "FB" | "IG"; image: string | null; type: string | null;
      account: string; text: string; reach: number; engagement: number; rate: number;
      link: string | null;
    }> = [];

    fbPosts.filter((p) => p.post_id && p.post_impressions_unique > 100).forEach((p) => {
      all.push({
        key: p.post_id || "", platform: "FB", image: p.post_picture, type: p.type,
        account: p.account_name, text: p.post_message || "—",
        reach: p.post_impressions_unique, engagement: p.page_post_engagements,
        rate: p.post_impressions_unique > 0 ? p.page_post_engagements / p.post_impressions_unique : 0,
        link: p.permalink_url || (p.post_id ? `https://facebook.com/${p.post_id}` : null),
      });
    });

    igPosts.filter((p) => p.media_id && p.media_reach > 100).forEach((p) => {
      all.push({
        key: p.media_id || "", platform: "IG", image: p.media_thumbnail_url || p.media_url, type: p.media_product_type || p.media_type,
        account: p.account_name, text: p.media_caption || "—",
        reach: p.media_reach, engagement: p.media_engagement,
        rate: p.media_reach > 0 ? p.media_engagement / p.media_reach : 0,
        link: p.media_permalink,
      });
    });

    return all.sort((a, b) => b.rate - a.rate).slice(0, limit);
  }, [fbPosts, igPosts, limit]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-green-500" />
          Viral posty
        </CardTitle>
        <p className="text-xs text-muted-foreground">Nejvyšší engagement rate (engagement / reach)</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Žádná data</p>
        ) : (
          items.map((p, i) => (
            <div key={`${p.key}-${i}`} className="flex gap-3 p-2 rounded-lg bg-secondary/30">
              <ImagePreview src={p.image} fallbackType={p.type} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className={`text-[10px] ${p.platform === "FB" ? "border-[#c9a84c]" : "border-[#d4816b]"}`}>
                    {p.platform}
                  </Badge>
                  <span className="text-xs text-muted-foreground truncate">{p.account}</span>
                  <span className="text-[10px] text-muted-foreground">{getPostTypeLabel(p.type)}</span>
                </div>
                <p className="text-xs truncate mb-1">{p.text}</p>
                <div className="flex gap-3 text-[11px]">
                  <span className="text-muted-foreground">Dosah: <b className="text-foreground">{fmt(p.reach)}</b></span>
                  <span className="text-muted-foreground">Eng: <b className="text-foreground">{fmt(p.engagement)}</b></span>
                  <span className="text-green-500 font-semibold">{fmtPct(p.rate)}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

// ====== Follower trend (per-page breakdown) ======

interface FollowerTrendProps {
  fbPosts?: FBPost[];
  igPosts?: IGPost[];
  platform?: "FB" | "IG";
  dateFrom?: string;
  dateTo?: string;
}

interface PageFollowers {
  account: string;
  first: number;
  last: number;
  delta: number;
}

function computePageFollowers<T extends { account_name: string; date: string }>(
  posts: T[],
  getCount: (p: T) => number
): PageFollowers[] {
  const byAccount = new Map<string, T[]>();
  posts.forEach((p) => {
    if (!p.account_name || !getCount(p)) return;
    if (!byAccount.has(p.account_name)) byAccount.set(p.account_name, []);
    byAccount.get(p.account_name)!.push(p);
  });

  const result: PageFollowers[] = [];
  byAccount.forEach((arr, account) => {
    const sorted = arr.slice().sort((a, b) => a.date.localeCompare(b.date));
    const first = getCount(sorted[0]);
    const last = getCount(sorted[sorted.length - 1]);
    result.push({ account, first, last, delta: last - first });
  });
  return result.sort((a, b) => b.last - a.last);
}

export function FollowerTrend({ fbPosts = [], igPosts = [], platform }: FollowerTrendProps) {
  const fbPages = useMemo(
    () => computePageFollowers(fbPosts, (p) => p.page_fans || 0),
    [fbPosts]
  );
  const igPages = useMemo(
    () => computePageFollowers(igPosts, (p) => p.followers_count || 0),
    [igPosts]
  );

  const showFb = (platform !== "IG") && fbPages.length > 0;
  const showIg = (platform !== "FB") && igPages.length > 0;

  if (!showFb && !showIg) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Trend followers</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">Žádná data o fanoušcích</p>
        </CardContent>
      </Card>
    );
  }

  const renderRow = (page: PageFollowers, prefix: "FB" | "IG") => (
    <div key={`${prefix}-${page.account}`} className="flex items-center gap-3 py-1.5 border-b border-border last:border-0">
      <span className={`text-[10px] font-bold px-1.5 rounded ${prefix === "FB" ? "text-[#c9a84c] bg-[#c9a84c]/10" : "text-[#d4816b] bg-[#d4816b]/10"}`}>
        {prefix}
      </span>
      <span className="text-sm flex-1 truncate">{page.account}</span>
      <span className="text-sm font-semibold tabular-nums">{fmt(page.last)}</span>
      <span className={`text-xs font-semibold tabular-nums w-[60px] text-right ${page.delta > 0 ? "text-green-500" : page.delta < 0 ? "text-red-400" : "text-muted-foreground"}`}>
        {page.delta > 0 ? "+" : ""}{fmt(page.delta)}
      </span>
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          {platform === "FB" ? "Fanoušci FB stránek" : platform === "IG" ? "Followers IG profilů" : "Trend followers"}
        </CardTitle>
        <p className="text-xs text-muted-foreground">Aktuální počet + změna za období (per stránka)</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-0">
          {showFb && fbPages.map((p) => renderRow(p, "FB"))}
          {showIg && igPages.map((p) => renderRow(p, "IG"))}
        </div>
      </CardContent>
    </Card>
  );
}
